import {
  HIGH_LEVEL_VALUES_TO_LABELS,
  METRIC_VALUES_TO_LABELS,
} from 'constants/metricValuesToLabels';
import { DESCENDING } from 'constants/sortDirections';
import {
  HighLevelMediumAgnosticMetrics,
  ListIdSegs,
  MediumOptions,
  MissingMetrics,
  NonMediumSpecificMetricOptionLabels,
} from 'modules/messagingInsights/constants';
import { Metric } from 'modules/messagingInsights/types';
import { getMetricMedium } from 'modules/messagingInsights/utils';
import { formatNumberGivenValueType } from 'modules/productAnalytics/utils';
import moment from 'moment';
import { displayDate } from 'utils/date';
import { isEmpty } from '@iterable/utils';
import {
  FormattedSegmentRespData,
  MultiMediumSegmentData,
  SegmentRespData,
  SegmentRows,
  Segment as SegmentType,
  SingleMediumSegmentData,
} from './types';

const EmailListName = 'EmailListName';
const All = 'All';
const Overall = 'Overall';
export const medAgnostic = 'Other/All mediums';

// Formats the values that we want to sort so that we can distinguish between 0, N/A and defined values
export const formatSortValue = (val: number | string | undefined) =>
  val === 0 || (val && val !== 'N/A') ? val : Number.MIN_SAFE_INTEGER;

// Correctly sorts rows when some cells are undefined
export const sortUndefinedRows = (rows: SegmentRows, sortedBy: string, sortDirection: string) =>
  rows.sort((val1, val2) => {
    let formattedValue1 = formatSortValue(val1[sortedBy]);
    let formattedValue2 = formatSortValue(val2[sortedBy]);
    if (typeof formattedValue1 === 'string' && typeof formattedValue2 === 'string') {
      formattedValue1 = formattedValue1.toLowerCase();
      formattedValue2 = formattedValue2.toLowerCase();
    }
    if (sortDirection === DESCENDING) {
      return formattedValue1 > formattedValue2 ? -1 : 1;
    }
    return formattedValue2 > formattedValue1 ? -1 : 1;
  });

export const formatSegmentName = (name: string, segmentType: string | undefined) => {
  if (Number.isNaN(parseInt(name, 10))) {
    return name;
  }
  switch (segmentType) {
    case 'date':
      return parseInt(name, 10);
    case 'long':
      return parseInt(name, 10);
    case 'double':
      return parseFloat(name);
    default:
      return name;
  }
};

/**
 * Currently, there are differences in what the BE endpoint returns for the overallMetrics segment data
 * depending on which segment is selected.
 *
 * If the 'emailListIds' or 'userListIds' segment is selected, overallMetrics has values that are formatted as objects.
 * overallMetrics: {
 *    seg1: { metric1: { value: 1, ...}, metric2: { value: 2, ...}}
 * }
 *
 * Whereas, for every other segment, overallMetrics has values that are formatted as an array of objects.
 * overallMetrics: {
 *    seg1: [{ metric1: { value: 1, ...}}, { metric2: { value: 2, ...}}]
 * }
 *
 * Eventually, BE will return all overallMetrics data (regardless of what seg is selected) with the first format (objects as values)
 * exhibited here.  But for now, FE will use the function below to rewrite any values that are presented as
 * an array of objects into just objects.
 */
export const formatSegOverallMetricsResp = (segmentRespData: SegmentRespData) => {
  const { overallMetrics = {} } = segmentRespData;

  const newOverallMetrics: { [segmentName: string]: MultiMediumSegmentData } = {};
  Object.entries(overallMetrics).forEach(([segment, metricVals]) => {
    if (Array.isArray(metricVals)) {
      const metricObj = metricVals.reduce((obj, metric) => Object.assign({}, obj, metric), {});
      newOverallMetrics[segment] = metricObj;
    } else {
      newOverallMetrics[segment] = metricVals;
    }
  });
  return { ...segmentRespData, overallMetrics: newOverallMetrics };
};

// Get the medium/medium-type that a metric is best categorized by
const getSpecificMetricMedium = (metric: string, analyticsMetrics: Metric[]) => {
  const medium = getMetricMedium(metric, analyticsMetrics);
  if (medium !== 'Other') {
    return MediumOptions[medium];
  } else if (
    HighLevelMediumAgnosticMetrics.has(metric) ||
    NonMediumSpecificMetricOptionLabels.has(metric)
  ) {
    return medAgnostic;
  } else {
    return Overall;
  }
};

type MetricMediums = { [metric: string]: string };
/**
 * Map a provided list of metrics to their corresponding mediums/medium-type
 * (e.g. {Unique Email Sends: Email, Total Purchases: Other/All Medium, Total Sent: Overall})
 */
export const getMetricMediums = (metrics: string[], analyticsMetrics: Metric[]): MetricMediums =>
  metrics.reduce(
    (acc, metric) => ({ ...acc, [metric]: getSpecificMetricMedium(metric, analyticsMetrics) }),
    {},
  );

type MetricValsByMed = { [medium: string]: { [metric: string]: string | number } };
/**
 * Maps mediums to an object that maps each selected metric to their corresponding default value.
 * This default value is either 0 or 'N/A', depending on the medium.
 * If the medium matches the medium corresponding to the metric, it is 0, otherwise, the default value is 'N/A'.
 * This mapping is later used to first populate CombinedSegData with default values, which can later
 * be overwritten with any metric values provided via the BE endpoint using the populateSegData helper function.
 */
export const getDefaultMetricValuesByMedium = (
  selectedMetrics: string[],
  mediums: string[],
  metricMediums: MetricMediums,
): MetricValsByMed =>
  mediums.reduce((acc, medium) => {
    const defaultMetricVals = selectedMetrics.reduce(
      (ac, metric) => ({ ...ac, [metric]: metricMediums[metric] === medium ? 0 : 'N/A' }),
      {},
    );
    return { ...acc, [medium]: defaultMetricVals };
  }, {});

type SegmentData = {
  [segmentName: string]: {
    [medium: string]: {
      [metricName: string]: number | string;
    };
  };
};

/**
 * Helper fxn for populating the segment --> medium --> metric mapping used in parseSegmentResp
 */
export const populateSegData = (
  combinedSegData: SegmentData,
  segMetricData: { [segName: string]: MultiMediumSegmentData | SingleMediumSegmentData },
  metricMediums: MetricMediums,
  mediums: string[],
  metrics: string[],
) => {
  Object.entries(segMetricData).forEach(([seg, segData]) => {
    Object.entries(segData).forEach(([metric, metricData]) => {
      const metricMedium = metricMediums[metric];
      /* eslint-disable no-param-reassign */
      if (metricData.mediums && metricMedium !== medAgnostic) {
        Object.entries(metricData.mediums as { [medium: string]: { value: number } }).forEach(
          ([medium, mediumData]) => {
            if (metrics.includes(metric) && mediums.includes(medium)) {
              combinedSegData[seg][medium][metric] = mediumData.value;
            }
          },
        );
      } else if (metric === EmailListName) {
        mediums.forEach((medium) => {
          combinedSegData[seg][medium][EmailListName] = metricData.value;
        });
      } else {
        // eslint-disable-next-line no-lonely-if
        if (metrics.includes(metric) && mediums.includes(metricMedium)) {
          combinedSegData[seg][metricMedium][metric] = metricData.value;
        }
      }
      /* eslint-enable no-param-reassign */
    });
  });
};

// Converts the response data from BE into the format usable by the TableRows component
export const parseSegmentResp = (
  segmentRespData: SegmentRespData,
  selectedMediums: string[],
  selectedMetrics: string[],
  selectedSegment: SegmentType,
  sortedBy: string,
  unsupportedMetrics: string[],
  analyticsMetrics: Metric[],
) => {
  if (isEmpty(segmentRespData)) {
    return [];
  }

  const newSegRespData: FormattedSegmentRespData = formatSegOverallMetricsResp(segmentRespData);
  const { overallMetrics, mediumMetrics } = newSegRespData;
  const combinedSegData: SegmentData = {};
  const metrics = selectedMetrics
    .map((metric) => METRIC_VALUES_TO_LABELS[metric] || HIGH_LEVEL_VALUES_TO_LABELS[metric])
    .filter(
      (metric) =>
        !MissingMetrics.has(metric) &&
        !unsupportedMetrics.includes(metric) &&
        !['Total Email Opens (filtered)', 'Unique Email Opens (filtered)'].includes(metric), // hide FPO metrics until BE segments fix
    );
  if (ListIdSegs.has(selectedSegment?.name || '')) {
    metrics.push(EmailListName);
  }
  const mediums = selectedMediums.map((med) => MediumOptions[med]);
  const haveMedAgnosticMetrics = metrics.filter(
    (metric) =>
      HighLevelMediumAgnosticMetrics.has(metric) || NonMediumSpecificMetricOptionLabels.has(metric),
  );
  if (haveMedAgnosticMetrics.length) {
    mediums.push(medAgnostic);
  }

  const metricMediums = getMetricMediums(metrics, analyticsMetrics);
  const defaultMetricValsByMed = getDefaultMetricValuesByMedium(metrics, mediums, metricMediums);

  const allSegments = [...new Set([...Object.keys(mediumMetrics), ...Object.keys(overallMetrics)])];
  allSegments.forEach((seg) => {
    combinedSegData[seg] = {};
    if (sortedBy === 'Segment') {
      // Adds a placeholder row that lists the metric name (w/o medium breakdowns)
      // Only show this row when sorting by Segment since the medium breakdown rows are grouped together
      combinedSegData[seg][All] = {};
    }

    mediums.forEach((med) => {
      combinedSegData[seg][med] = { ...defaultMetricValsByMed[med] };
    });
  });

  populateSegData(combinedSegData, overallMetrics, metricMediums, mediums, metrics);
  populateSegData(combinedSegData, mediumMetrics, metricMediums, mediums, metrics);

  // Re-configure mappings into the table format
  const segmentRows: Record<string, string | number>[] = [];
  Object.entries(combinedSegData).forEach(([seg, medData]) => {
    Object.entries(medData).forEach(([medium, metricData]) => {
      const formattedName = formatSegmentName(seg, selectedSegment?.type);
      segmentRows.push({ id: `${seg}-${medium}`, Segment: formattedName, medium, ...metricData });
    });
  });

  return segmentRows;
};

// Correctly format CSV data so that it matches the table in the app
export const getCSVdata = (
  sortedRows: SegmentRows,
  segmentIsDate: boolean,
  selectedMetrics: string[],
  selectedSegment: SegmentType,
  ValueTypes: Record<string, string>,
) => {
  const csvMetrics = ListIdSegs.has(selectedSegment?.name || '')
    ? [...selectedMetrics, EmailListName]
    : selectedMetrics;

  return sortedRows.map((row) => {
    const { Segment, medium } = row;
    const segment = segmentIsDate
      ? displayDate(moment.unix(parseInt(Segment as string, 10) / 1000))
      : Segment;
    const segmentWithMedium = medium === All ? segment : `${segment} (${medium})`;

    const metricData: Record<string, string> = {};
    csvMetrics.forEach((metric) => {
      metricData[metric] =
        row.medium === All ? '' : formatNumberGivenValueType(ValueTypes[metric], row[metric]);
    });

    return { Segment: segmentWithMedium, ...metricData };
  });
};

export const getSegmentCount = (segmentData: SegmentRespData) => {
  if (!segmentData) {
    return 0;
  }

  const { mediumMetrics = {}, overallMetrics = {} } = segmentData || {};
  return [...new Set([...Object.keys(mediumMetrics), ...Object.keys(overallMetrics)])].length;
};
